/**
 * useCheckout Hook - React hook for checkout operations in App-client
 * Provides state management and context-aware checkout functionality for mobile app
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { checkoutService, CheckoutContext } from '../services/CheckoutService';

/**
 * Checkout states
 */
export const CheckoutState = {
  IDLE: 'idle',
  LOADING_SESSION: 'loading_session',
  PROCESSING_PAYMENT: 'processing_payment',
  SUCCESS: 'success',
  ERROR: 'error'
};

/**
 * useCheckout Hook - Mobile-optimized checkout hook
 * @param {string} context - Default checkout context (defaults to mobile-app)
 * @param {Object} options - Hook configuration options
 * @returns {Object} Checkout state and operations
 */
export const useCheckout = (context = CheckoutContext.MOBILE_APP, options = {}) => {
  const {
    onSessionCreated = null,
    onPaymentSuccess = null,
    onPaymentError = null,
    autoReset = true,
    resetDelay = 3000,
    enableOfflineMode = false
  } = options;

  // State management
  const [state, setState] = useState(CheckoutState.IDLE);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [lastPaymentResult, setLastPaymentResult] = useState(null);
  const [offlineQueue, setOfflineQueue] = useState([]);

  // Refs for cleanup
  const resetTimeoutRef = useRef(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
  }, []);

  /**
   * Reset checkout state
   */
  const reset = useCallback(() => {
    setState(CheckoutState.IDLE);
    setSession(null);
    setError(null);
    setLastPaymentResult(null);
    
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  /**
   * Auto-reset after success/error
   */
  const scheduleAutoReset = useCallback(() => {
    if (autoReset && resetDelay > 0) {
      resetTimeoutRef.current = setTimeout(reset, resetDelay);
    }
  }, [autoReset, resetDelay, reset]);

  /**
   * Add operation to offline queue
   */
  const addToOfflineQueue = useCallback((operation) => {
    if (enableOfflineMode) {
      setOfflineQueue(prev => [...prev, { ...operation, timestamp: new Date().toISOString() }]);
    }
  }, [enableOfflineMode]);

  /**
   * Create checkout session
   * @param {Array} groupIds - Appointment group IDs
   * @param {string} sessionContext - Optional context override
   * @returns {Promise<Object>} Session data
   */
  const createSession = useCallback(async (groupIds, sessionContext = context) => {
    try {
      setState(CheckoutState.LOADING_SESSION);
      setError(null);

      const sessionData = await checkoutService.createCheckoutSession(groupIds, sessionContext);
      
      setSession(sessionData);
      setState(CheckoutState.IDLE);
      
      if (onSessionCreated) {
        onSessionCreated(sessionData);
      }
      
      return sessionData;
    } catch (err) {
      setError(err.message);
      setState(CheckoutState.ERROR);
      
      // Add to offline queue if enabled
      addToOfflineQueue({
        type: 'CREATE_SESSION',
        groupIds,
        context: sessionContext
      });
      
      scheduleAutoReset();
      throw err;
    }
  }, [context, onSessionCreated, addToOfflineQueue, scheduleAutoReset]);

  /**
   * Process payment
   * @param {Object} paymentData - Payment details
   * @param {string} paymentContext - Optional context override
   * @returns {Promise<Object>} Payment result
   */
  const processPayment = useCallback(async (paymentData, paymentContext = context) => {
    try {
      setState(CheckoutState.PROCESSING_PAYMENT);
      setError(null);

      const result = await checkoutService.processPayment(paymentData, paymentContext);
      
      setLastPaymentResult(result);
      setState(CheckoutState.SUCCESS);
      
      if (onPaymentSuccess) {
        onPaymentSuccess(result);
      }
      
      scheduleAutoReset();
      return result;
    } catch (err) {
      setError(err.message);
      setState(CheckoutState.ERROR);
      
      // Add to offline queue if enabled
      addToOfflineQueue({
        type: 'PROCESS_PAYMENT',
        paymentData,
        context: paymentContext
      });
      
      if (onPaymentError) {
        onPaymentError(err);
      }
      
      scheduleAutoReset();
      throw err;
    }
  }, [context, onPaymentSuccess, onPaymentError, addToOfflineQueue, scheduleAutoReset]);

  /**
   * Get checkout preview
   * @param {Array} groupIds - Appointment group IDs
   * @param {string} previewContext - Optional context override
   * @returns {Promise<Object>} Preview data
   */
  const getPreview = useCallback(async (groupIds, previewContext = context) => {
    try {
      const preview = await checkoutService.getCheckoutPreview(groupIds, previewContext);
      return preview;
    } catch (err) {
      setError(err.message);
      
      // Add to offline queue if enabled
      addToOfflineQueue({
        type: 'GET_PREVIEW',
        groupIds,
        context: previewContext
      });
      
      throw err;
    }
  }, [context, addToOfflineQueue]);

  /**
   * Process offline queue when back online
   */
  const processOfflineQueue = useCallback(async () => {
    if (!enableOfflineMode || offlineQueue.length === 0) {
      return;
    }

    const results = [];
    for (const operation of offlineQueue) {
      try {
        let result;
        switch (operation.type) {
          case 'CREATE_SESSION':
            result = await createSession(operation.groupIds, operation.context);
            break;
          case 'PROCESS_PAYMENT':
            result = await processPayment(operation.paymentData, operation.context);
            break;
          case 'GET_PREVIEW':
            result = await getPreview(operation.groupIds, operation.context);
            break;
          default:
            continue;
        }
        results.push({ ...operation, result, status: 'success' });
      } catch (err) {
        results.push({ ...operation, error: err.message, status: 'failed' });
      }
    }

    // Clear successful operations from queue
    const failedOperations = results.filter(r => r.status === 'failed');
    setOfflineQueue(failedOperations.map(({ result, status, ...op }) => op));

    return results;
  }, [enableOfflineMode, offlineQueue]);

  // Context-specific convenience methods (mobile-optimized)
  const kanban = {
    createSession: (groupIds) => createSession(groupIds, CheckoutContext.KANBAN),
    processPayment: (paymentData) => processPayment(paymentData, CheckoutContext.KANBAN),
    getPreview: (groupIds) => getPreview(groupIds, CheckoutContext.KANBAN)
  };

  const appointmentsPage = {
    createSession: (groupIds) => createSession(groupIds, CheckoutContext.APPOINTMENTS_PAGE),
    processPayment: (paymentData) => processPayment(paymentData, CheckoutContext.APPOINTMENTS_PAGE),
    getPreview: (groupIds) => getPreview(groupIds, CheckoutContext.APPOINTMENTS_PAGE)
  };

  const mobileApp = {
    createSession: (groupIds) => createSession(groupIds, CheckoutContext.MOBILE_APP),
    processPayment: (paymentData) => processPayment(paymentData, CheckoutContext.MOBILE_APP),
    getPreview: (groupIds) => getPreview(groupIds, CheckoutContext.MOBILE_APP)
  };

  // Computed state
  const isLoading = state === CheckoutState.LOADING_SESSION || state === CheckoutState.PROCESSING_PAYMENT;
  const isSuccess = state === CheckoutState.SUCCESS;
  const isError = state === CheckoutState.ERROR;
  const hasSession = session !== null;
  const hasOfflineOperations = offlineQueue.length > 0;

  return {
    // State
    state,
    isLoading,
    isSuccess,
    isError,
    hasSession,
    hasOfflineOperations,
    session,
    error,
    lastPaymentResult,
    offlineQueue,

    // Actions
    createSession,
    processPayment,
    getPreview,
    reset,
    processOfflineQueue,

    // Context-specific operations
    kanban,
    appointmentsPage,
    mobileApp,

    // Constants for convenience
    CheckoutState,
    CheckoutContext
  };
};

/**
 * Specialized hooks for specific contexts
 */
export const useKanbanCheckout = (options = {}) => {
  return useCheckout(CheckoutContext.KANBAN, options);
};

export const useAppointmentsPageCheckout = (options = {}) => {
  return useCheckout(CheckoutContext.APPOINTMENTS_PAGE, options);
};

export const useMobileAppCheckout = (options = {}) => {
  return useCheckout(CheckoutContext.MOBILE_APP, options);
};